import { useState, useEffect, useRef } from "react";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import useCurrentUser from "hooks/useCurrentUser";
import { toast } from "react-toastify";
import * as faceapi from "@vladmandic/face-api";
import {
  PageContainer, PremiumCard, PremiumModal, FormSection, Field, SelectField,
  Button, StatusBadge, Avatar,
} from "components/ui";

const IconCamera = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);
const IconEdit = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);
const IconLock = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

// Minimum face bounding-box size (relative to image width) below which we
// warn the user that the photo may be too small/angled for reliable
// recognition at the kiosk. Purely advisory — upload is never blocked.
const MIN_FACE_RATIO = 0.15;

const Profile = () => {
  const axiosPrivate = useAxiosPrivate();
  const { user: currentUser } = useCurrentUser();

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [avatarModal, setAvatarModal] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState("");
  const [previousPreview, setPreviousPreview] = useState("");
  const [faceDetected, setFaceDetected] = useState(null);
  const [faceDescriptor, setFaceDescriptor] = useState(null); // Float32Array -> plain array (128 floats)
  const [faceTooSmall, setFaceTooSmall] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [modelsLoaded, setModelsLoaded] = useState(false);

  const [passwordModal, setPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ oldPassword: "", newPassword: "" });
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);

  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", phone: "",
    age: "", cin: "", avatarUrl: "",
    jobTitle: "", department: "", shift: "", shiftStart: "", shiftEnd: "",
  });

  useEffect(() => {
    const loadModels = async () => {
      try {
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri("/models"),
          faceapi.nets.faceLandmark68Net.loadFromUri("/models"),
          faceapi.nets.faceRecognitionNet.loadFromUri("/models"),
        ]);
        setModelsLoaded(true);
      } catch (_) {
        setModelsLoaded(false);
      }
    };
    loadModels();
  }, []);

  useEffect(() => {
    if (currentUser) {
      setForm({
        firstName: currentUser.firstName || "", lastName: currentUser.lastName || "",
        email: currentUser.email || "", phone: currentUser.phone || "",
        age: currentUser.age || "", cin: currentUser.cin || "",
        avatarUrl: currentUser.avatarUrl || "",
        jobTitle: currentUser.jobTitle || "", department: currentUser.department || "",
        shift: currentUser.shift || "",
        shiftStart: currentUser.shiftStart ? currentUser.shiftStart.substring(0, 5) : "",
        shiftEnd: currentUser.shiftEnd ? currentUser.shiftEnd.substring(0, 5) : "",
      });
      if (currentUser.avatarUrl) {
        setAvatarPreview(currentUser.avatarUrl);
        setPreviousPreview(currentUser.avatarUrl);
      }
    }
  }, [currentUser]);

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleCancelAvatar = () => {
    setAvatarPreview(previousPreview);
    setAvatarFile(null);
    setFaceDetected(null);
    setFaceDescriptor(null);
    setFaceTooSmall(false);
    setAvatarModal(false);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPreviousPreview(avatarPreview);
    const reader = new FileReader();
    reader.onload = () => setAvatarPreview(reader.result);
    reader.readAsDataURL(file);

    if (!modelsLoaded) {
      toast.warn("Face detection unavailable. You can still upload the image.");
      setFaceDetected(false);
      setFaceDescriptor(null);
      setFaceTooSmall(false);
      setAvatarFile(file);
      setAvatarModal(true);
      return;
    }

    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = async () => {
      try {
        // Chain landmarks + descriptor onto the same detection pass used
        // for the avatar preview — this descriptor is what the attendance
        // kiosk will later match against via faceapi.FaceMatcher.
        const detection = await faceapi
          .detectSingleFace(img, new faceapi.TinyFaceDetectorOptions())
          .withFaceLandmarks()
          .withFaceDescriptor();

        setFaceDetected(!!detection);

        if (detection) {
          setFaceDescriptor(Array.from(detection.descriptor));

          const faceWidthRatio = detection.detection.box.width / img.width;
          setFaceTooSmall(faceWidthRatio < MIN_FACE_RATIO);
        } else {
          setFaceDescriptor(null);
          setFaceTooSmall(false);
          toast.warning("No face detected.");
        }
      } catch (_) {
        setFaceDetected(false);
        setFaceDescriptor(null);
        setFaceTooSmall(false);
      } finally {
        setAvatarFile(file);
        setAvatarModal(true);
      }
    };
  };

  const uploadAvatar = async () => {
    if (!avatarFile) return null;
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append("file", avatarFile);
      if (faceDescriptor) {
        fd.append("faceDescriptor", JSON.stringify(faceDescriptor));
      }
      const response = await axiosPrivate.post("/api/users/upload-avatar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Avatar uploaded successfully");
      return response.data?.url;
    } catch (err) {
      toast.error("Upload failed");
      return null;
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let newAvatarUrl = form.avatarUrl;
      if (avatarFile) {
        const url = await uploadAvatar();
        if (url) newAvatarUrl = url;
      }
      const payload = {
        ...form, avatarUrl: newAvatarUrl,
        shiftStart: form.shiftStart ? form.shiftStart + ":00" : null,
        shiftEnd: form.shiftEnd ? form.shiftEnd + ":00" : null,
      };
      await axiosPrivate.put(`/api/users/${currentUser.id}`, payload);
      toast.success("Profile updated successfully");
      setEditing(false);
      setAvatarFile(null);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async () => {
    const { oldPassword, newPassword } = passwordForm;
    if (!oldPassword || !newPassword) { toast.warn("Both fields are required"); return; }
    if (newPassword.length < 6) { toast.warn("New password must be at least 6 characters"); return; }
    setPasswordSubmitting(true);
    try {
      await axiosPrivate.post("/api/users/change-password", { oldPassword, newPassword });
      toast.success("Password changed successfully");
      setPasswordModal(false);
      setPasswordForm({ oldPassword: "", newPassword: "" });
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to change password");
    } finally {
      setPasswordSubmitting(false);
    }
  };

  const displayName = `${form.firstName} ${form.lastName}`.trim() || "—";
  const initials = displayName === "—" ? "?" : displayName.split(/\s+/).map((n) => n[0]).slice(0, 2).join("").toUpperCase();

  return (
    <PageContainer>
      {/* Hero card with avatar + identity */}
      <PremiumCard className="mb-3" flush>
        <div
          style={{
            position: "relative",
            background: "linear-gradient(135deg, var(--brand-primary), var(--brand-accent))",
            height: 140,
            overflow: "hidden",
          }}
        >
          <div
            aria-hidden="true"
            style={{
              position: "absolute", inset: 0,
              backgroundImage: "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)",
              backgroundSize: "32px 32px",
              maskImage: "radial-gradient(ellipse at center, rgba(0,0,0,0.6) 0%, transparent 70%)",
            }}
          />
        </div>

        <div style={{ padding: "0 32px 28px", marginTop: -56, position: "relative", display: "flex", alignItems: "flex-end", gap: 20, flexWrap: "wrap" }}>
          {/* Avatar */}
          <button
            type="button"
            onClick={() => editing && fileInputRef.current?.click()}
            disabled={!editing}
            aria-label="Change avatar"
            style={{
              position: "relative",
              width: 112, height: 112, borderRadius: 24,
              border: "4px solid var(--surface)",
              boxShadow: "var(--shadow-lg)",
              background: "linear-gradient(135deg, var(--brand-primary), var(--brand-accent))",
              color: "#fff",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 32,
              cursor: editing ? "pointer" : "default",
              overflow: "hidden",
              padding: 0,
            }}
          >
            {avatarPreview ? (
              <img src={avatarPreview} alt="avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : initials}
            {editing && (
              <span
                style={{
                  position: "absolute", right: -4, bottom: -4,
                  width: 32, height: 32, borderRadius: 999,
                  background: "var(--brand-primary)",
                  color: "#fff",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  border: "3px solid var(--surface)",
                  boxShadow: "var(--shadow-md)",
                }}
              >
                <IconCamera />
              </span>
            )}
          </button>
          <input ref={fileInputRef} hidden type="file" accept="image/*" onChange={handleFileSelect} />

          {/* Identity */}
          <div style={{ flex: 1, minWidth: 0, paddingBottom: 8 }}>
            <h1 className="t-heading-lg" style={{ margin: 0 }}>{displayName}</h1>
            <p className="t-body-sm t-muted" style={{ margin: "4px 0 8px" }}>{form.email}</p>
            {currentUser?.role && <StatusBadge value={currentUser.role} />}
          </div>

          {/* Actions */}
          <div style={{ display: "inline-flex", gap: 8, paddingBottom: 8, flexWrap: "wrap" }}>
            {editing ? (
              <>
                <Button variant="secondary" onClick={() => setEditing(false)}>Cancel</Button>
                <Button variant="primary" loading={saving} onClick={handleSave}>Save changes</Button>
              </>
            ) : (
              <>
                <Button variant="secondary" leftIcon={<IconLock />} onClick={() => setPasswordModal(true)}>Change password</Button>
                <Button variant="primary" leftIcon={<IconEdit />} onClick={() => setEditing(true)}>Edit profile</Button>
              </>
            )}
          </div>
        </div>

        {/* Quick stats */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            borderTop: "1px solid var(--border-subtle)",
            background: "var(--surface-muted)",
          }}
        >
          {[
            { label: "Department", value: form.department },
            { label: "Job title", value: form.jobTitle },
            { label: "Shift", value: form.shift },
          ].map((s, i) => (
            <div
              key={s.label}
              style={{
                padding: "16px 24px",
                borderRight: i < 2 ? "1px solid var(--border-subtle)" : "none",
              }}
            >
              <div className="t-body-xs">{s.label}</div>
              <div style={{ marginTop: 4, fontWeight: 600, color: "var(--text-primary)" }}>
                {s.value || "—"}
              </div>
            </div>
          ))}
        </div>
      </PremiumCard>

      {/* Form sections */}
      <PremiumCard>
        <div style={{ display: "grid", gap: 24 }}>
          <FormSection title="Personal information" columns={2}>
            <Field label="First name" value={form.firstName} onChange={change("firstName")} disabled={!editing} />
            <Field label="Last name" value={form.lastName} onChange={change("lastName")} disabled={!editing} />
            <Field label="Email" type="email" value={form.email} onChange={change("email")} disabled={!editing} />
            <Field label="Phone" value={form.phone} onChange={change("phone")} disabled={!editing} />
            <Field label="Age" type="number" value={form.age} onChange={change("age")} disabled={!editing} />
            <Field label="CIN" value={form.cin} onChange={change("cin")} disabled={!editing} />
          </FormSection>

          <FormSection title="Work details" columns={2}>
            <Field label="Job title" value={form.jobTitle} onChange={change("jobTitle")} disabled={!editing} />
            <Field label="Department" value={form.department} onChange={change("department")} disabled={!editing} />
            <SelectField label="Shift" value={form.shift} onChange={change("shift")} disabled={!editing}>
              <option value="">— None —</option>
              <option value="MORNING">Morning</option>
              <option value="AFTERNOON">Afternoon</option>
              <option value="NIGHT">Night</option>
            </SelectField>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="Start" type="time" value={form.shiftStart} onChange={change("shiftStart")} disabled={!editing} />
              <Field label="End" type="time" value={form.shiftEnd} onChange={change("shiftEnd")} disabled={!editing} />
            </div>
          </FormSection>
        </div>
      </PremiumCard>

      {/* AVATAR MODAL */}
      <PremiumModal
        isOpen={avatarModal}
        toggle={handleCancelAvatar}
        title="Confirm avatar"
        footer={
          <>
            <Button variant="secondary" onClick={handleCancelAvatar}>Cancel</Button>
            {(faceDetected || !modelsLoaded) && (
              <Button
                variant="primary"
                loading={uploadingAvatar}
                onClick={async () => {
                  const url = await uploadAvatar();
                  if (url) {
                    setForm((prev) => ({ ...prev, avatarUrl: url }));
                    setAvatarModal(false);
                    toast.success("Avatar updated! Save profile to persist.");
                  }
                }}
              >
                Upload avatar
              </Button>
            )}
          </>
        }
      >
        <div style={{ textAlign: "center" }}>
          <img
            src={avatarPreview}
            alt="preview"
            style={{
              width: "100%", maxHeight: 320, objectFit: "cover",
              borderRadius: 16, marginBottom: 16,
            }}
          />
          <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "center" }}>
            {!modelsLoaded ? (
              <StatusBadge tone="warning">Face detection unavailable</StatusBadge>
            ) : faceDetected ? (
              <StatusBadge tone="success">Face detected</StatusBadge>
            ) : (
              <StatusBadge tone="warning">No face detected</StatusBadge>
            )}

            {faceDetected && faceDescriptor && (
              <p className="t-body-xs t-muted" style={{ margin: 0 }}>
                This photo will also be used for attendance kiosk recognition.
              </p>
            )}

            {faceDetected && faceTooSmall && (
              <StatusBadge tone="warning">
                Face is small/angled — a closer, front-facing photo will recognize better at the kiosk
              </StatusBadge>
            )}
          </div>
        </div>
      </PremiumModal>

      {/* PASSWORD MODAL */}
      <PremiumModal
        isOpen={passwordModal}
        toggle={() => setPasswordModal(false)}
        title="Change password"
        footer={
          <>
            <Button variant="secondary" onClick={() => setPasswordModal(false)}>Cancel</Button>
            <Button variant="primary" loading={passwordSubmitting} onClick={handlePasswordSubmit}>Update password</Button>
          </>
        }
      >
        <div style={{ display: "grid", gap: 16 }}>
          <Field
            label="Current password" required type="password"
            value={passwordForm.oldPassword}
            onChange={(e) => setPasswordForm((p) => ({ ...p, oldPassword: e.target.value }))}
            placeholder="Enter current password"
            autoComplete="current-password"
          />
          <Field
            label="New password" required type="password"
            value={passwordForm.newPassword}
            onChange={(e) => setPasswordForm((p) => ({ ...p, newPassword: e.target.value }))}
            placeholder="At least 6 characters"
            autoComplete="new-password"
          />
        </div>
      </PremiumModal>
    </PageContainer>
  );
};

export default Profile;